import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  alpha,
  styled
} from '@mui/material';
import {
  FileDownload as ExportIcon,
  Close as CloseIcon,
  DateRange as DateIcon,
  GetApp as DownloadIcon,
  CheckCircle as CheckIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import apiService from 'Network/apiService';
import { API_ENDPOINTS } from 'Network/apiEndpoints';

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  '& .MuiCardContent-root': {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2)
    }
  }
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1, 0),
  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  '& .MuiSvgIcon-root': {
    color: theme.palette.primary.main,
    fontSize: '1.2rem'
  },
  '& .MuiTypography-root': {
    fontWeight: 600,
    color: theme.palette.primary.dark
  }
}));

const LAB_VISIT_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
];

const EnquiryFormExportModal = ({ open, onClose, onRefresh }) => {
  const isCompanyUser = apiService.isCompanyUser();
  const loggedCompanyId = apiService.getCompanyId();

  // State for filters
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Date range state
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [dateFilter, setDateFilter] = useState('thisMonth');
  const [executives, setExecutives] = useState([{ label: 'All', value: 'all' }]);
  const [selectedExecutive, setSelectedExecutive] = useState('all');
  const [loadingExecutives, setLoadingExecutives] = useState(false);

  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loadingCategories, setLoadingCategories] = useState(false);

  const [labVisit, setLabVisit] = useState('all');

  const fetchExecutives = async () => {
    setLoadingExecutives(true);
    try {
      const response = await apiService.get(API_ENDPOINTS.GET_enquiry_executives);
      const list =
        response?.data?.executives ||
        response?.data?.data?.executives ||
        (Array.isArray(response?.data) ? response.data : []);
      if (Array.isArray(list) && list.length > 0) {
        setExecutives(list);
        const hasAll = list.some((item) => String(item?.value) === 'all');
        setSelectedExecutive(hasAll ? 'all' : String(list[0]?.value ?? 'all'));
      } else {
        setExecutives([{ label: 'All', value: 'all' }]);
        setSelectedExecutive('all');
      }
    } catch (err) {
      console.error('Error fetching enquiry executives:', err);
      setExecutives([{ label: 'All', value: 'all' }]);
      setSelectedExecutive('all');
    } finally {
      setLoadingExecutives(false);
    }
  };

  const fetchCompanies = useCallback(async () => {
    if (isCompanyUser) {
      const details = apiService.getCompanyDetails();
      const id = loggedCompanyId;
      setCompanyOptions([
        {
          id,
          name: details?.name || 'My Company',
        },
      ]);
      setSelectedCompany(id ? String(id) : '');
      return;
    }

    setLoadingCompanies(true);
    try {
      const response = await apiService.get(API_ENDPOINTS.GET_company_choices);
      const options = response?.data?.Company || response?.data?.choices || response?.data || [];
      setCompanyOptions(Array.isArray(options) ? options : []);
    } catch (err) {
      console.error('Error fetching companies:', err);
      setCompanyOptions([]);
    } finally {
      setLoadingCompanies(false);
    }
  }, [isCompanyUser, loggedCompanyId]);

  const fetchCategories = useCallback(async (companyId) => {
    if (!companyId) {
      setCategories([]);
      return;
    }

    setLoadingCategories(true);
    try {
      const userId = localStorage.getItem('user_id');
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_product_category}?company=${companyId}&user_id=${userId}`
      );
      if (response?.success) {
        setCategories(response.data?.ProductCategory || []);
      } else {
        setCategories([]);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  // Handle date filter changes
  const handleDateFilterChange = (filterType) => {
    setDateFilter(filterType);
    const today = new Date();
    const formatLocalDate = (date) =>
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const todayStr = formatLocalDate(today);

    switch (filterType) {
      case 'thisMonth': {
        const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        setDateRange({
          startDate: formatLocalDate(thisMonthStart),
          endDate: todayStr,
        });
        break;
      }
      case 'lastMonth': {
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        setDateRange({
          startDate: formatLocalDate(lastMonthStart),
          endDate: formatLocalDate(lastMonthEnd),
        });
        break;
      }
      case 'last3Months': {
        const threeMonthsStart = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        setDateRange({
          startDate: formatLocalDate(threeMonthsStart),
          endDate: todayStr,
        });
        break;
      }
      case 'custom':
        setDateRange({
          startDate: '',
          endDate: todayStr,
        });
        break;
      default:
        break;
    }
  };

  // Initialize filters when modal opens
  useEffect(() => {
    if (open) {
      handleDateFilterChange('thisMonth');
      setSelectedExecutive('all');
      setSelectedCategory('');
      setLabVisit('all');
      if (!isCompanyUser) {
        setSelectedCompany('');
      }
      fetchExecutives();
      fetchCompanies();
    }
  }, [open, isCompanyUser, fetchCompanies]);

  // Fetch categories when company changes
  useEffect(() => {
    if (!open) return;
    setSelectedCategory('');
    if (selectedCompany) {
      fetchCategories(selectedCompany);
    } else {
      setCategories([]);
    }
  }, [open, selectedCompany, fetchCategories]);

  // Validate date range
  const validateDateRange = () => {
    const today = new Date().toISOString().split('T')[0];
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    const todayDate = new Date(today);

    if (startDate > todayDate) {
      setError('Start date cannot be in the future');
      return false;
    }

    if (endDate > todayDate) {
      setError('End date cannot be in the future');
      return false;
    }

    if (startDate > endDate) {
      setError('Start date cannot be after end date');
      return false;
    }

    return true;
  };

  // Handle export submission
  const handleExport = async () => {
    if (!validateDateRange()) {
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const params = new URLSearchParams({
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
        executive: selectedExecutive || 'all',
      });

      if (selectedCompany) {
        params.set('company_id', selectedCompany);
      }
      if (selectedCategory) {
        params.set('category_id', selectedCategory);
      }
      if (labVisit && labVisit !== 'all') {
        params.set('lab_visit', labVisit);
      }

      console.log('Enquiry Form Export params:', Object.fromEntries(params.entries()));

      const endpoint = `${API_ENDPOINTS.GET_export_excel_enquiry_form}?${params.toString()}`;
      const filename = `enquiry_form_export_${dateRange.startDate}_to_${dateRange.endDate}.xlsx`;

      await apiService.downloadFile(endpoint, filename);

      setSuccess('Enquiry Form data exported successfully!');
      setTimeout(() => {
        handleClose();
        onRefresh?.();
      }, 2000);
    } catch (exportError) {
      console.error('Error exporting enquiry form data:', exportError);
      setError(exportError.message || 'Failed to export enquiry form data. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setError('');
      setSuccess('');
      setDateFilter('thisMonth');
      setSelectedExecutive('all');
      setSelectedCategory('');
      setLabVisit('all');
      if (!isCompanyUser) {
        setSelectedCompany('');
      }
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ExportIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Export Enquiry Form Data
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={submitting}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Select date range and filters to export enquiry form data to Excel
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        <Box sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} icon={<CheckIcon />}>
              {success}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Date Range Filter */}
            <Grid item xs={12}>
              <StyledCard>
                <CardContent>
                  <SectionHeader>
                    <DateIcon />
                    <Typography variant="h6">Date Range</Typography>
                  </SectionHeader>

                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <RadioGroup
                      row
                      value={dateFilter}
                      onChange={(e) => handleDateFilterChange(e.target.value)}
                      sx={{ gap: 2 }}
                    >
                      <FormControlLabel value="custom" control={<Radio />} label="Custom Range" />
                      <FormControlLabel value="thisMonth" control={<Radio />} label="This Month" />
                      <FormControlLabel value="lastMonth" control={<Radio />} label="Last Month" />
                      <FormControlLabel value="last3Months" control={<Radio />} label="Last 3 Months" />
                    </RadioGroup>
                  </FormControl>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        type="date"
                        value={dateRange.startDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        disabled={dateFilter !== 'custom'}
                        inputProps={{
                          max: new Date().toISOString().split('T')[0]
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="End Date"
                        type="date"
                        value={dateRange.endDate}
                        onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        disabled={dateFilter !== 'custom'}
                        inputProps={{
                          max: new Date().toISOString().split('T')[0],
                          min: dateRange.startDate
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Note:</strong> Only current date and past dates are allowed. Future dates are not permitted.
                    </Typography>
                  </Alert>
                </CardContent>
              </StyledCard>
            </Grid>

            {/* Additional Filters */}
            <Grid item xs={12}>
              <StyledCard>
                <CardContent>
                  <SectionHeader>
                    <FilterIcon />
                    <Typography variant="h6">Filters</Typography>
                  </SectionHeader>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" disabled={isCompanyUser || loadingCompanies || submitting}>
                        <InputLabel id="enquiry-export-company-label">Company</InputLabel>
                        <Select
                          labelId="enquiry-export-company-label"
                          label="Company"
                          value={selectedCompany}
                          onChange={(e) => setSelectedCompany(e.target.value)}
                        >
                          {!isCompanyUser && (
                            <MenuItem value="">All</MenuItem>
                          )}
                          {loadingCompanies ? (
                            <MenuItem value={selectedCompany} disabled>
                              Loading companies...
                            </MenuItem>
                          ) : (
                            companyOptions.map((company) => (
                              <MenuItem key={String(company.id)} value={String(company.id)}>
                                {company.name}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl
                        fullWidth
                        size="small"
                        disabled={!selectedCompany || loadingCategories || submitting}
                      >
                        <InputLabel id="enquiry-export-category-label">Category</InputLabel>
                        <Select
                          labelId="enquiry-export-category-label"
                          label="Category"
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                          <MenuItem value="">All</MenuItem>
                          {loadingCategories ? (
                            <MenuItem value={selectedCategory} disabled>
                              Loading categories...
                            </MenuItem>
                          ) : (
                            categories.map((category) => (
                              <MenuItem key={String(category.id)} value={String(category.id)}>
                                {category.name}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" disabled={submitting}>
                        <InputLabel id="enquiry-export-lab-visit-label">Lab Visit</InputLabel>
                        <Select
                          labelId="enquiry-export-lab-visit-label"
                          label="Lab Visit"
                          value={labVisit}
                          onChange={(e) => setLabVisit(e.target.value)}
                        >
                          {LAB_VISIT_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small" disabled={loadingExecutives || submitting}>
                        <InputLabel id="enquiry-export-executive-label">Executive</InputLabel>
                        <Select
                          labelId="enquiry-export-executive-label"
                          label="Executive"
                          value={selectedExecutive}
                          onChange={(e) => setSelectedExecutive(e.target.value)}
                        >
                          {loadingExecutives ? (
                            <MenuItem value={selectedExecutive} disabled>
                              Loading executives...
                            </MenuItem>
                          ) : (
                            executives.map((item) => (
                              <MenuItem key={String(item.value)} value={String(item.value)}>
                                {item.label || item.value}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </StyledCard>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : <DownloadIcon />}
          sx={{ textTransform: 'none', minWidth: 120 }}
        >
          {submitting ? 'Exporting...' : 'Export to Excel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EnquiryFormExportModal;
